import React from 'react';
import { EstimateEditor, STATUS_CONFIG, calcLineTotals } from '../EstimatePanel';
import { activeEstimate, moneyState, invoiceDueMs, invoiceOverdueMs } from '../../platform/engine/estimateMoney';
import { buildApprovalSmsHref, buildPaymentReminderSmsHref } from './roMoney';
import { fmtTs, fmtAgo } from './roHelpers';
import MoneyCard from './MoneyCard';

const NEUTRAL_CHIP = { color: '#64748b', bg: '#f1f5f9' };

// The right-column money rail: five stacked cards — Estimate, Labor, Parts,
// Invoice, Comms. The Estimate card is a launcher for EstimatePanel's
// full-screen builder/invoice overlay (it's too wide for the 320px rail);
// the others are read-mostly summaries whose actions open that overlay,
// switch tabs, or fire the same sms:/mailto: deep links used elsewhere —
// none of them duplicate payment/invoice state-mutating logic.
export default function MoneyRail({ truck, settings, canEdit, onSaveEstimate, now, onOpenPartsTab, expanded, onExpand, estimateMode = 'builder', customerTermsDays = null, poRequired = false }) {
  const toggle = (id) => onExpand(expanded === id ? null : id);
  // Two doors to the one editor: the Estimate card opens the builder, the
  // Invoice card opens straight to the invoice document (preview).
  const openEstimate = () => onExpand('estimate', 'builder');
  const openInvoice  = () => onExpand('estimate', 'preview');

  const est = activeEstimate(truck);
  const { stage, total, paid, remaining } = moneyState(truck);
  const sc = STATUS_CONFIG[stage] || NEUTRAL_CHIP;

  const laborHours = est ? (est.lines || []).reduce((s, ln) => s + calcLineTotals(ln, truck.parts).laborHours, 0) : 0;
  const laborAmount = est ? (est.lines || []).reduce((s, ln) => s + calcLineTotals(ln, truck.parts).labor, 0) : 0;

  const dueMs = est ? invoiceDueMs(est) : null;
  const overdueMs = est ? invoiceOverdueMs(est, now) : 0;

  const partsTotal = (truck.parts || []).reduce((s, p) => s + (parseFloat(p.price) || 0) * (parseFloat(p.quantity) || 1), 0);

  const commsLog = (truck.activities || [])
    .filter(a => a.customerFacing || a.type === 'authorization')
    .slice(0, 5);

  return (
    <div className="ro-money-rail">

      {/* The estimate/invoice builder is a wide, multi-column form — it can't
          live in the 320px rail. The card is a launcher; the editor opens as
          its full-screen overlay (rendered below). */}
      <MoneyCard
        title="Estimate"
        chipLabel={est ? sc.label : 'None'}
        chipColor={est ? sc.color : NEUTRAL_CHIP.color}
        chipBg={est ? sc.bg : NEUTRAL_CHIP.bg}
        actionLabel={est ? 'Open' : 'Build Estimate'}
        onAction={openEstimate}
        expanded={false}
        onToggle={openEstimate}
      />

      <MoneyCard
        title="Labor"
        chipLabel={laborHours > 0 ? `${laborHours.toFixed(1)}h · $${laborAmount.toFixed(0)}` : 'None logged'}
        chipColor={NEUTRAL_CHIP.color} chipBg={NEUTRAL_CHIP.bg}
        actionLabel="Edit in Estimate"
        onAction={openEstimate}
        expanded={expanded === 'labor'}
        onToggle={() => toggle('labor')}
      >
        {est?.lines?.length ? (
          <table className="ro-mcard-table">
            <thead><tr><th>Operation</th><th className="ro-mcard-r">Hours</th><th className="ro-mcard-r">$</th></tr></thead>
            <tbody>
              {est.lines.map((ln, i) => {
                const lt = calcLineTotals(ln, truck.parts);
                return (
                  <tr key={i}>
                    <td>{ln.title || `Operation ${i + 1}`}</td>
                    <td className="ro-mcard-r">{lt.laborHours ? lt.laborHours.toFixed(1) : '—'}</td>
                    <td className="ro-mcard-r">${lt.labor.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : <p className="ro-mcard-empty">No labor logged yet.</p>}
        <button type="button" className="ro-mcard-link" onClick={openEstimate}>Edit in Estimate →</button>
      </MoneyCard>

      {/* Parts is a wide table too — launcher for the full-width Parts tab,
          same treatment as the Estimate card. */}
      <MoneyCard
        title="Parts"
        chipLabel={truck.parts?.length ? `${truck.parts.length} · $${partsTotal.toFixed(0)}` : 'None'}
        chipColor={NEUTRAL_CHIP.color} chipBg={NEUTRAL_CHIP.bg}
        actionLabel="Manage Parts"
        onAction={onOpenPartsTab}
        expanded={false}
        onToggle={onOpenPartsTab}
      />

      <MoneyCard
        title="Invoice"
        chipLabel={
          stage === 'paid'     ? 'Paid in full' :
          stage === 'invoiced' ? (overdueMs > 0 ? 'Overdue' : `Due ${fmtTs(est.invoicedAt)}`) :
          truck.status === 'done' ? 'Ready to invoice' :
          'Not yet'
        }
        chipColor={stage === 'paid' ? '#15803d' : overdueMs > 0 ? '#dc2626' : NEUTRAL_CHIP.color}
        chipBg={stage === 'paid' ? '#dcfce7' : overdueMs > 0 ? '#fee2e2' : NEUTRAL_CHIP.bg}
        actionLabel={
          stage === 'invoiced' ? 'Record Payment' :
          (truck.status === 'done' && !['invoiced', 'paid'].includes(stage)) ? 'Generate Invoice' :
          null
        }
        onAction={openInvoice}
        expanded={expanded === 'invoice'}
        onToggle={() => toggle('invoice')}
      >
        {est ? (
          <dl className="ro-mcard-kv">
            <dt>Total</dt><dd>${total.toFixed(2)}</dd>
            {paid > 0 && <><dt>Paid</dt><dd>${paid.toFixed(2)}</dd></>}
            {stage === 'invoiced' && <><dt>Balance</dt><dd>${remaining.toFixed(2)}</dd></>}
            {est.invoicedAt && <><dt>Invoiced</dt><dd>{fmtTs(est.invoicedAt)}</dd></>}
            {dueMs && <><dt>Due</dt><dd>{new Date(dueMs).toLocaleDateString()}</dd></>}
          </dl>
        ) : <p className="ro-mcard-empty">No invoice yet.</p>}
        <button type="button" className="ro-mcard-link" onClick={openInvoice}>Open Invoice →</button>
      </MoneyCard>

      <MoneyCard
        title="Comms"
        chipLabel={commsLog.length ? `${commsLog.length} logged` : 'None'}
        chipColor={NEUTRAL_CHIP.color} chipBg={NEUTRAL_CHIP.bg}
        actionLabel={
          stage === 'sent'                          ? 'Resend Approval Text' :
          (stage === 'invoiced' && remaining > 0)    ? 'Send Payment Reminder' :
          null
        }
        onAction={() => {
          const href = stage === 'sent'
            ? buildApprovalSmsHref(truck, est)
            : buildPaymentReminderSmsHref(truck, est);
          if (href) window.location.href = href;
        }}
        expanded={expanded === 'comms'}
        onToggle={() => toggle('comms')}
      >
        {commsLog.length ? (
          <ul className="ro-mcard-comms">
            {commsLog.map(a => (
              <li key={a.id}>
                <span className="ro-mcard-comms-time">{fmtAgo(a.timestamp, now)} ago</span>
                <span className="ro-mcard-comms-text">{a.text}</span>
              </li>
            ))}
          </ul>
        ) : <p className="ro-mcard-empty">No customer communication logged yet.</p>}
      </MoneyCard>

      {/* Full-screen estimate/invoice builder — opened by the Estimate card
          and the Labor/Invoice "Open Estimate" actions. Overlay (no `inline`
          prop) so it gets real width instead of the 320px rail. */}
      {expanded === 'estimate' && (
        <EstimateEditor
          truck={truck}
          onClose={() => onExpand(null)}
          onSave={onSaveEstimate}
          settings={settings}
          canEdit={canEdit}
          initialMode={estimateMode}
          customerTermsDays={customerTermsDays}
          poRequired={poRequired}
        />
      )}

    </div>
  );
}
